import React from 'react';
import { confirmSignUp, signIn, signOut, resendSignUpCode } from 'aws-amplify/auth';
import { trackEvent } from './utils/analytics';

/**
 * ConfirmPage — one-tap email confirmation landing page (link-only signup flow).
 *
 * Reached when a user taps the "Confirm my email" link in the signup email
 * (the TaleboundCognitoCustomEmail Lambda builds /confirm?t=<base64url({u,c})>).
 * Confirms the account, then shows a single password field and signs the user
 * straight into the app (no landing-page bounce). Confirmation is link-only —
 * there is no 6-digit code path anymore.
 *
 * Rendered standalone (no app shell) from index.js; Amplify is already
 * configured there before this renders.
 */
// The signup email links to /confirm?t=<base64url({u: email, c: code})>.
// We use an opaque token (not ?u=email&c=code) so the URL carries no plaintext
// email address — a literal email in a link is a spam/phishing signal and leaks
// PII into logs, history, and referrer headers.
function decodeToken(token) {
  if (!token) return {};
  try {
    let b64 = token.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '='; // base64url strips padding; atob needs it back
    const obj = JSON.parse(decodeURIComponent(escape(atob(b64))));
    return { email: obj.u, code: obj.c };
  } catch (e) {
    return {};
  }
}

export default function ConfirmPage({ token }) {
  const { email, code } = React.useMemo(() => decodeToken(token), [token]);
  const [state, setState] = React.useState('confirming'); // confirming | success | already | error
  const [msg, setMsg] = React.useState('');
  const [pw, setPw] = React.useState('');
  const [signingIn, setSigningIn] = React.useState(false);
  const [signinErr, setSigninErr] = React.useState('');
  const [resendMsg, setResendMsg] = React.useState('');

  // The account is confirmed; the user sets/enters their password right here and we
  // sign them straight into the app — no bounce to a cold landing/sign-in page.
  const handleSignIn = async (e) => {
    if (e) e.preventDefault();
    if (!pw) { setSigninErr('Enter your password.'); return; }
    setSigningIn(true); setSigninErr('');
    try {
      let r;
      try {
        r = await signIn({ username: email, password: pw });
      } catch (inner) {
        // Another account (e.g. one confirmed earlier in the same browser) may already be
        // signed in — Amplify then throws "There is already a signed in user." Clear that
        // session and sign in fresh as the just-confirmed account.
        const inName = inner && inner.name ? inner.name : '';
        if (inName === 'UserAlreadyAuthenticatedException' || /already.*signed in/i.test((inner && inner.message) || '')) {
          await signOut();
          r = await signIn({ username: email, password: pw });
        } else {
          throw inner;
        }
      }
      if (r && r.isSignedIn) {
        // Funnel events (moved here from the removed AuthPage code-entry path):
        // 'success' = just confirmed via the link → the user's first authenticated session.
        trackEvent('login', { method: state === 'success' ? 'post_signup' : 'password' });
        if (state === 'success') trackEvent('first_login', { method: 'email' });
        window.location.replace('/'); // straight into the app, signed in
      } else {
        // signIn returned an additional step (e.g. TOTP/MFA) that this page has no UI
        // for. MFA is off platform-wide today, so this is purely defensive: don't
        // redirect as if signed in — send them to the normal sign-in, which handles
        // MFA challenges (AuthPage's totp mode).
        setSigningIn(false);
        setSigninErr('Your account needs an extra verification step. Please sign in from the Talebound homepage.');
      }
    } catch (err) {
      const n = err && err.name ? err.name : '';
      if (n === 'NotAuthorizedException') setSigninErr('That password didn’t match. Try again.');
      else if (n === 'UserNotConfirmedException') setSigninErr('Almost there — tap the email link again.');
      else setSigninErr((err && err.message) || 'Could not sign in. Please try again.');
      setSigningIn(false);
    }
  };

  // Resend a fresh confirmation link (used from the error state, e.g. an expired link).
  const handleResend = async () => {
    if (!email) return;
    setResendMsg('Sending…');
    try {
      await resendSignUpCode({ username: email });
      setResendMsg('Sent! Check your email (and spam) for a fresh link.');
    } catch (er) {
      if (/CONFIRMED/i.test((er && er.message) || '')) {
        setResendMsg('This account is already confirmed — just sign in.');
      } else {
        setResendMsg('Could not resend. Please sign up again.');
      }
    }
  };

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!email || !code) {
        setState('error');
        setMsg('This link looks incomplete. Please tap the link in your email again, or sign up again.');
        return;
      }
      try {
        await confirmSignUp({ username: email, confirmationCode: code });
        if (!cancelled) setState('success');
      } catch (err) {
        if (cancelled) return;
        const name = err && err.name ? err.name : '';
        const m = err && err.message ? err.message : '';
        if (name === 'NotAuthorizedException' && /CONFIRMED/i.test(m)) {
          setState('already');
        } else if (name === 'ExpiredCodeException') {
          setState('error');
          setMsg('This confirmation link has expired. Please sign in to request a fresh one, or sign up again.');
        } else if (name === 'CodeMismatchException') {
          setState('error');
          setMsg("This link didn't match. Please tap the link in your email again, or request a new one.");
        } else if (name === 'UserNotFoundException') {
          setState('error');
          setMsg("We couldn't find that account. Please sign up again.");
        } else {
          setState('error');
          setMsg(m || 'Something went wrong confirming your email. Please tap the link in your email again, or sign up again.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [email, code]);

  const headline = {
    confirming: 'Confirming your email…',
    success: "You're all set!",
    already: 'Already confirmed!',
    error: 'Hmm, that didn’t work',
  }[state];

  const body = {
    confirming: 'One moment while we confirm your account.',
    success: 'Your email is confirmed. Enter your password to jump in and tell your first story.',
    already: 'You’re already confirmed. Enter your password to jump back in.',
    error: msg,
  }[state];

  return (
    <div style={{
      minHeight: '100svh', background: 'radial-gradient(120% 70% at 50% 12%, #FFF6E2 0%, #FBF1E0 45%, #F4E7D0 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      fontFamily: "'Lora', Georgia, serif", color: '#0f172a',
    }}>
      <div style={{
        maxWidth: 440, width: '100%', background: '#fff', borderRadius: 20, padding: '40px 34px 34px',
        textAlign: 'center', boxShadow: '0 18px 50px rgba(120,70,20,0.16)', border: '1px solid rgba(201,168,76,0.25)',
      }}>
        <img src="/pip/pip-face.png" alt="Pip" style={{ width: 84, height: 84, objectFit: 'contain', marginBottom: 6, filter: 'drop-shadow(0 8px 14px rgba(120,70,20,0.18))' }} />

        {state === 'confirming' && (
          <div style={{ margin: '14px auto', width: 30, height: 30, border: '3px solid rgba(201,168,76,0.3)', borderTopColor: '#c9a84c', borderRadius: '50%', animation: 'cpspin 0.8s linear infinite' }} />
        )}
        {(state === 'success' || state === 'already') && (
          <div style={{ fontSize: 44, lineHeight: 1, margin: '6px 0 10px', color: '#4a8a4a' }}>✓</div>
        )}
        {state === 'error' && (
          <div style={{ fontSize: 40, lineHeight: 1, margin: '6px 0 10px' }}>📩</div>
        )}

        <h1 style={{ fontFamily: "'Lora', serif", fontSize: '1.7rem', fontWeight: 600, margin: '4px 0 10px', letterSpacing: '0.02em' }}>{headline}</h1>
        <p style={{ fontSize: '1.02rem', color: '#4e3f2c', lineHeight: 1.6, margin: '0 0 26px' }}>{body}</p>

        {(state === 'success' || state === 'already') && (
          <form onSubmit={handleSignIn} style={{ marginTop: 2 }}>
            <input
              type="password" value={pw} autoFocus autoComplete="current-password"
              onChange={e => { setPw(e.target.value); if (signinErr) setSigninErr(''); }}
              placeholder="Your password"
              style={{ width: '100%', padding: '14px 16px', fontSize: '1.05rem', fontFamily: "'Lora', serif",
                border: '1.5px solid rgba(44,32,22,0.2)', borderRadius: 14, marginBottom: 10, outline: 'none', textAlign: 'center' }}
            />
            {signinErr && <p style={{ color: '#c0392b', fontSize: '0.9rem', margin: '0 0 10px' }}>{signinErr}</p>}
            <button type="submit" disabled={signingIn} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%',
              background: 'linear-gradient(135deg, #F0A24C, #D2761F)', color: '#fff', border: 'none', cursor: 'pointer',
              fontFamily: "'Nunito', 'Lora', sans-serif", fontWeight: 800, fontSize: '1.1rem',
              padding: '15px 30px', borderRadius: 18, boxShadow: '0 10px 24px rgba(210,118,31,0.4)', opacity: signingIn ? 0.7 : 1,
            }}>{signingIn ? 'Signing in…' : 'Start writing →'}</button>
            <a href="/" onClick={() => { try { localStorage.setItem('tb_prefill_email', email); } catch (er) {} }}
              style={{ display: 'inline-block', marginTop: 14, fontSize: '0.9rem', color: '#7a6b54', textDecoration: 'underline' }}>
              Forgot your password?
            </a>
          </form>
        )}
        {state === 'error' && (
          <div>
            {email && (
              <>
                <button type="button" onClick={handleResend} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', cursor: 'pointer',
                  background: 'linear-gradient(135deg, #F0A24C, #D2761F)', color: '#fff', border: 'none',
                  fontFamily: "'Nunito', 'Lora', sans-serif", fontWeight: 800, fontSize: '1.05rem',
                  padding: '14px 28px', borderRadius: 16, boxShadow: '0 10px 24px rgba(210,118,31,0.4)', marginBottom: 12,
                }}>Resend confirmation link</button>
                {resendMsg && <p style={{ fontSize: '0.9rem', color: '#4e3f2c', margin: '0 0 14px' }}>{resendMsg}</p>}
              </>
            )}
            <a href="/" style={{
              display: 'inline-block', color: '#7a6b54', textDecoration: 'underline',
              fontFamily: "'Lora', serif", fontSize: '0.95rem',
            }}>Go to sign in</a>
          </div>
        )}
      </div>
      <style>{`@keyframes cpspin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
